import React, { useEffect, useRef, useState } from "react";
import { Mic, Trash2, Play, Square, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * VoiceDropsPanel — record short host audio (intros, station IDs, drops)
 * and play them between tracks during a show.
 *
 * Records via MediaRecorder, encodes to a base64 data-URI, posts to
 * /api/dj/voice-drops. Loaded list is cached in component state and the
 * `onPlayDrop(dataUri)` callback is invoked by the parent so the drop
 * routes through the studio's audio engine like any other deck source.
 *
 * Capped backend-side at 256 KB / drop and 12 drops / creator. UI shows
 * the cap so users know.
 */
export default function VoiceDropsPanel({ onPlayDrop }) {
  const [drops, setDrops] = useState([]);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUri, setRecordedUri] = useState(null);
  const [recordSec, setRecordSec] = useState(0);
  const [label, setLabel] = useState("");
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickerRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/dj/voice-drops");
      setDrops(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
        video: false,
      });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setRecordedBlob(blob);
        const reader = new FileReader();
        reader.onloadend = () => setRecordedUri(reader.result);
        reader.readAsDataURL(blob);
      };
      mr.start();
      recRef.current = mr;
      startedAtRef.current = Date.now();
      setRecordSec(0);
      tickerRef.current = setInterval(() => {
        const s = Math.floor((Date.now() - startedAtRef.current) / 1000);
        setRecordSec(s);
        // Hard-stop at 15s — any longer and we risk bumping the 256 KB cap.
        if (s >= 15) stopRec();
      }, 200);
      setRecording(true);
    } catch (e) {
      toast.error("Mic permission denied");
    }
  };
  const stopRec = () => {
    try { recRef.current?.stop(); } catch { /* ignore */ }
    if (tickerRef.current) clearInterval(tickerRef.current);
    setRecording(false);
  };
  const discardRec = () => {
    setRecordedBlob(null); setRecordedUri(null); setRecordSec(0); setLabel("");
  };
  // Upload an existing audio file as a voice drop. Server caps at
  // 256 KB / drop so we sanity-check on the client before reading the
  // file into a data-URI to avoid wasting cycles on a too-big upload.
  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("audio/")) {
      toast.error("Pick an audio file (MP3, WAV, OGG, M4A, WEBM).");
      e.target.value = "";
      return;
    }
    const MAX = 256 * 1024;
    if (file.size > MAX) {
      toast.error(`File too large (${Math.round(file.size / 1024)} KB > ${MAX / 1024} KB). Trim or re-export at lower quality.`);
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setRecordedUri(reader.result);
      setRecordSec(0);
      // Pre-fill label from file name (sans extension) so the host can
      // tweak instead of typing from scratch.
      if (!label.trim()) {
        const name = file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ").trim();
        setLabel(name.slice(0, 80));
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const saveRec = async () => {
    if (!recordedUri || !label.trim()) {
      toast.error("Add a label first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/dj/voice-drops", {
        label: label.trim(),
        data_uri: recordedUri,
        duration_sec: recordSec || null,
      });
      setDrops((cur) => [data, ...cur]);
      discardRec();
      toast.success("Voice drop saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };
  const removeDrop = async (id) => {
    if (!window.confirm("Delete this voice drop?")) return;
    try {
      await api.delete(`/dj/voice-drops/${id}`);
      setDrops((cur) => cur.filter((d) => d.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section data-testid="dj-voice-drops-panel" className="rounded-xl border border-white/10 bg-black/40 p-3 mt-3">
      <header className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Mic className="w-4 h-4 text-amber-300" />
          <h3 className="text-[11px] uppercase tracking-[0.25em] font-black text-white/85">Voice drops</h3>
          <span className="text-[10px] text-white/45">{drops.length}/12</span>
        </div>
      </header>
      {/* Recorder */}
      <div className="bg-white/5 border border-white/10 rounded p-2 mb-2 space-y-1.5">
        {!recordedUri ? (
          <div className="grid grid-cols-2 gap-1.5">
            <button
              data-testid="dj-voice-drops-record"
              onClick={recording ? stopRec : startRec}
              className={`inline-flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-wider py-1.5 rounded border transition ${
                recording
                  ? "bg-red-500/30 text-red-100 border-red-400/60 animate-pulse"
                  : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
              }`}
            >
              {recording
                ? (<><Square className="w-3 h-3" /> Stop ({recordSec}s)</>)
                : (<><Mic className="w-3 h-3" /> Record (15s)</>)}
            </button>
            <button
              data-testid="dj-voice-drops-upload"
              onClick={() => fileInputRef.current?.click()}
              disabled={recording}
              className="inline-flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-wider py-1.5 rounded border bg-white/5 text-white/70 border-white/10 hover:bg-white/10 disabled:opacity-40"
            >
              <Upload className="w-3 h-3" /> Upload
            </button>
            <input
              ref={fileInputRef}
              data-testid="dj-voice-drops-file-input"
              type="file"
              accept="audio/*"
              onChange={handleUpload}
              className="hidden"
            />
          </div>
        ) : (
          <div className="space-y-1.5">
            <audio src={recordedUri} controls className="w-full h-7" data-testid="dj-voice-drops-preview" />
            <div className="flex items-center gap-1.5">
              <input
                data-testid="dj-voice-drops-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Label (e.g. 'Tap In Radio ID')"
                maxLength={80}
                disabled={busy}
                className="flex-1 bg-black/40 border border-white/10 outline-none rounded px-2 py-1 text-[11px] text-white"
              />
              <button
                data-testid="dj-voice-drops-save"
                onClick={saveRec}
                disabled={busy || !label.trim()}
                title="Save drop"
                className="bg-emerald-500/30 hover:bg-emerald-500 disabled:opacity-40 text-emerald-100 hover:text-black border border-emerald-400/60 px-2 py-1 rounded"
              >
                <Save className="w-3.5 h-3.5" />
              </button>
              <button onClick={discardRec} title="Discard" className="text-white/40 hover:text-rose-300 px-1">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Library */}
      {drops.length > 0 && (
        <ul className="space-y-1 max-h-[160px] overflow-y-auto">
          {drops.map((d) => (
            <li key={d.id} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-2 py-1 text-[11px]">
              <button
                data-testid={`dj-voice-drops-play-${d.id}`}
                onClick={() => onPlayDrop?.(d)}
                title="Play through master"
                className="text-amber-300 hover:text-amber-100 p-0.5"
              >
                <Play className="w-3.5 h-3.5" />
              </button>
              <span className="flex-1 truncate text-white/85">{d.label}</span>
              {d.duration_sec ? <span className="text-white/40 text-[10px] tabular-nums">{Math.round(d.duration_sec)}s</span> : null}
              <button
                onClick={() => removeDrop(d.id)}
                title="Delete"
                className="text-white/35 hover:text-rose-300 p-0.5"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
